/**
 * AllMarketNewsPage Component
 *
 * Displays a list of general market news articles.
 * Reuses MarketNewsList for consistent styling with the /markets tab.
 */

import { FC, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { useHistory } from 'react-router-dom';
import { colors, spacing, fontStyles } from '../../styles';
import Page from '../Page';
import Header from '../Header';
import MarketNewsList from '../MarketNewsList';
import { getGeneralMarketNews, PortfolioNewsArticle } from '../../apis/backend';

const AllMarketNewsPage: FC = () => {
  const history = useHistory();
  const [newsArticles, setNewsArticles] = useState<PortfolioNewsArticle[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchNews = async () => {
      setIsLoading(true);
      try {
        const response = await getGeneralMarketNews();
        if (isMounted) {
          setNewsArticles(response.articles || []);
        }
      } catch (error) {
        console.error('[AllMarketNewsPage] Failed to fetch news:', error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchNews();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Page showNavigationBar={false}>
      <Header headingText="Market News" onBack={() => history.goBack()} variant="left-aligned" />

      <div
        css={css`
          max-width: 600px;
          margin: 0 auto;
          padding: ${spacing.deci};
        `}
      >
        <MarketNewsList newsArticles={newsArticles} isLoading={isLoading} maxItems={50} />

        {!isLoading && newsArticles.length > 0 && (
          <div
            css={css`
              display: flex;
              justify-content: center;
              padding: ${spacing.deci};
              color: ${colors.silver};
              ${fontStyles.mouse}
              html[data-theme='dark'] & {
                color: ${colors.tungsten};
              }
            `}
          >
            You&apos;re all caught up!
          </div>
        )}
      </div>
    </Page>
  );
};

export default AllMarketNewsPage;
